import { useApolloClient, useMutation } from "@apollo/client";
import { ButtonBase, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ARCHIVE_NOTIFICATION,
  GET_NOTIFICATION_USER,
  MARK_ALL_NOTIFICATIONS_READ,
  MARK_ALL_NOTIFICATIONS_UNREAD,
  MARK_NOTIFICATION_READ,
  MARK_NOTIFICATION_UNREAD,
} from "../../hooks/useNotifications";
import { useAlerts } from "../Alerts/AlertProvider";
import { getNotificationDestination, type NotificationOpenTarget } from "./notificationNavigation";
import { getNotificationSubjectDetail } from "./notificationPresentation";

const BOUNCE_MS = 520;

/** Whether the bell is mid-bounce, and a way to start (or restart) one. */
export function useBellBounce(): [boolean, () => void] {
  const [bouncing, setBouncing] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const bounce = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setBouncing(true);
    timeoutRef.current = setTimeout(() => {
      setBouncing(false);
      timeoutRef.current = null;
    }, BOUNCE_MS);
  }, []);

  return [bouncing, bounce];
}

export type UnreadCount = {
  unreadCount: number;
  /** Shifts the shown count ahead of the server, e.g. -1 when a notification is read. */
  adjust: (delta: number) => void;
  /** Pins the shown count, or with null goes back to the server's. */
  set: (count: number | null) => void;
};

/**
 * The unread count the bell shows: the server's, adjusted optimistically while a
 * change is in flight. Refetched whenever the window comes back into focus.
 */
export function useUnreadCount(serverUnreadCount: number, refetch: () => unknown): UnreadCount {
  const [optimistic, setOptimistic] = useState<number | null>(null);
  const [previousServerCount, setPreviousServerCount] = useState(serverUnreadCount);
  // Once the server says nothing is unread, there's nothing left to be ahead of it about.
  if (serverUnreadCount !== previousServerCount) {
    setPreviousServerCount(serverUnreadCount);
    if (serverUnreadCount === 0) {
      setOptimistic(null);
    }
  }

  useEffect(() => {
    const handleFocus = () => {
      setOptimistic(null);
      void refetch();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleFocus();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refetch]);

  return {
    unreadCount: optimistic ?? serverUnreadCount,
    adjust: (delta) =>
      setOptimistic((previous) => Math.max(0, (previous ?? serverUnreadCount) + delta)),
    set: setOptimistic,
  };
}

/**
 * Toasts for new notifications. The handlers are refs because a toast outlives the
 * render that created it and must reach the current ones, which close the tray first.
 */
export function useNotificationToasts(
  openNotification: React.RefObject<(target: NotificationOpenTarget) => void>,
  openInbox: React.RefObject<() => void>
) {
  const alerts = useAlerts();
  const apolloClient = useApolloClient();

  const showNotificationToast = useCallback(
    async (notificationUserId: string) => {
      let title = "New notification";
      let subjectDetail: string | null = null;
      let target: NotificationOpenTarget = {
        id: notificationUserId,
        readAt: null,
        notification: {},
      };

      try {
        const result = await apolloClient.query({
          query: GET_NOTIFICATION_USER,
          variables: { id: notificationUserId },
          fetchPolicy: "network-only",
        });
        const notificationUser = result.data.notificationUser;
        if (notificationUser) {
          target = notificationUser;
          title = notificationUser.notification.title ?? title;
          subjectDetail = getNotificationSubjectDetail(notificationUser.notification);
        }
      } catch {
        // The stream event is enough to route; the lookup only improves the toast copy.
      }

      alerts.info(
        <ButtonBase
          onClick={() => openNotification.current?.(target)}
          sx={{
            color: "inherit",
            display: "block",
            maxWidth: 320,
            textAlign: "left",
          }}
        >
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
            {title}
          </Typography>
          {subjectDetail ? (
            <Typography variant="body2" sx={{ opacity: 0.8, overflowWrap: "anywhere" }}>
              {subjectDetail}
            </Typography>
          ) : null}
        </ButtonBase>,
        { autoHideDuration: 8000 }
      );
    },
    [alerts, apolloClient, openNotification]
  );

  const showBurstToast = useCallback(
    (count: number) => {
      alerts.info(
        <ButtonBase
          onClick={() => openInbox.current?.()}
          sx={{ color: "inherit", display: "block", maxWidth: 320, textAlign: "left" }}
        >
          <Typography variant="body2">{count} new notifications</Typography>
        </ButtonBase>,
        { autoHideDuration: 8000 }
      );
    },
    [alerts, openInbox]
  );

  return { showNotificationToast, showBurstToast };
}

type ReceiptTarget = { id: string; readAt?: unknown };

const nowIso = () => new Date().toISOString();

/** A receipt change's stream echo is expected within this long of the mutation. */
const OWN_ECHO_WINDOW_MS = 10_000;

/**
 * The stream echoes this tab's own receipt changes back to it. Each change expects
 * exactly one echo (the server emits one per mutation), so an entry is used up by
 * the first matching event; anything after it is a change made elsewhere.
 */
function useOwnEchoes() {
  const expectedRef = useRef(new Map<string, number[]>());

  return useMemo(() => {
    const expected = expectedRef.current;
    const live = (notificationUserId: string) =>
      (expected.get(notificationUserId) ?? []).filter((deadline) => deadline > Date.now());
    const store = (notificationUserId: string, deadlines: number[]) => {
      if (deadlines.length === 0) {
        expected.delete(notificationUserId);
      } else {
        expected.set(notificationUserId, deadlines);
      }
    };

    return {
      expect: (notificationUserId: string) =>
        store(notificationUserId, [...live(notificationUserId), Date.now() + OWN_ECHO_WINDOW_MS]),
      forget: (notificationUserId: string) =>
        store(notificationUserId, live(notificationUserId).slice(1)),
      /** Whether this event is the echo of a change made here, using it up if so. */
      consume: (notificationUserId: string) => {
        const deadlines = live(notificationUserId);
        store(notificationUserId, deadlines.slice(1));
        return deadlines.length > 0;
      },
    };
  }, []);
}

/**
 * Reading, unreading and archiving from the tray, each shown optimistically. The
 * mutation results update the cached rows, so reads leave the list where it is;
 * an archive refreshes it so the page refills.
 */
export function useNotificationReceiptActions({
  unread,
  refreshCount,
  refreshList,
  close,
}: {
  unread: UnreadCount;
  refreshCount: () => Promise<unknown>;
  refreshList: () => void;
  close: () => void;
}) {
  const navigate = useNavigate();
  const alerts = useAlerts();
  const ownEchoes = useOwnEchoes();
  const [updatingNotificationId, setUpdatingNotificationId] = useState<string | null>(null);
  const [markRead] = useMutation(MARK_NOTIFICATION_READ);
  const [markUnread] = useMutation(MARK_NOTIFICATION_UNREAD);
  const [archiveNotification] = useMutation(ARCHIVE_NOTIFICATION);
  const [markAllRead, markAllReadState] = useMutation(MARK_ALL_NOTIFICATIONS_READ);
  const [markAllUnread, markAllUnreadState] = useMutation(MARK_ALL_NOTIFICATIONS_UNREAD);

  const inFlightRef = useRef(0);

  /**
   * Runs a receipt mutation, saying so if it fails. Once the refreshed count is in and
   * nothing else from here is still in flight, the server's count replaces the
   * optimistic one, so a change counted twice can't leave the bell wrong.
   */
  const settle = async (failureMessage: string, mutation: () => Promise<unknown>) => {
    inFlightRef.current += 1;
    let succeeded = false;
    try {
      await mutation();
      succeeded = true;
    } catch (error) {
      alerts.error(failureMessage, error);
    }
    await refreshCount().catch(() => undefined);
    inFlightRef.current -= 1;
    if (inFlightRef.current === 0) {
      unread.set(null);
    }
    return succeeded;
  };

  const updateReceipt = async (
    notificationUserId: string,
    failureMessage: string,
    mutation: () => Promise<unknown>
  ) => {
    setUpdatingNotificationId(notificationUserId);
    ownEchoes.expect(notificationUserId);
    const succeeded = await settle(failureMessage, mutation);
    if (!succeeded) {
      ownEchoes.forget(notificationUserId);
    }
    setUpdatingNotificationId((currentId) => (currentId === notificationUserId ? null : currentId));
    return succeeded;
  };

  const read = (notificationUserId: string) =>
    updateReceipt(notificationUserId, "Couldn't mark the notification as read", () =>
      markRead({
        variables: { notificationUserId },
        optimisticResponse: {
          markNotificationRead: {
            __typename: "NotificationUser" as const,
            id: notificationUserId,
            readAt: nowIso(),
          },
        },
      })
    );

  const unreadOne = (notificationUserId: string) =>
    updateReceipt(notificationUserId, "Couldn't mark the notification as unread", () =>
      markUnread({
        variables: { notificationUserId },
        optimisticResponse: {
          markNotificationUnread: {
            __typename: "NotificationUser" as const,
            id: notificationUserId,
            readAt: null,
          },
        },
      })
    );

  const markAll = (
    mutation: () => Promise<unknown>,
    optimisticCount: number | null,
    failureMessage: string
  ) => {
    unread.set(optimisticCount);
    close();
    void settle(failureMessage, mutation);
  };

  return {
    consumeOwnEcho: ownEchoes.consume,
    updatingNotificationId,
    markingAllRead: markAllReadState.loading,
    markingAllUnread: markAllUnreadState.loading,
    // Opening a notification marks it read and navigates to its subject in one
    // action — there is no interstitial detail page to do the mark-read for us.
    open: (target: NotificationOpenTarget) => {
      if (!target.readAt) {
        unread.adjust(-1);
        void read(target.id);
      }
      close();
      navigate(getNotificationDestination(target));
    },
    toggleRead: (target: ReceiptTarget) => {
      const wasRead = Boolean(target.readAt);
      unread.adjust(wasRead ? 1 : -1);
      void (wasRead ? unreadOne(target.id) : read(target.id));
    },
    archive: (target: ReceiptTarget) => {
      if (!target.readAt) {
        unread.adjust(-1);
      }
      void updateReceipt(target.id, "Couldn't archive the notification", () =>
        archiveNotification({
          variables: { notificationUserId: target.id },
          optimisticResponse: {
            archiveNotification: {
              __typename: "NotificationUser" as const,
              id: target.id,
              readAt: nowIso(),
              archivedAt: nowIso(),
            },
          },
        })
      ).then((succeeded) => {
        if (succeeded) {
          refreshList();
        }
      });
    },
    markAllRead: () =>
      markAll(() => markAllRead({ variables: {} }), 0, "Couldn't mark all notifications as read"),
    markAllUnread: () =>
      markAll(
        () => markAllUnread({ variables: {} }),
        null,
        "Couldn't mark all notifications as unread"
      ),
  };
}

/** Which tray row's actions menu is open, and where it's anchored. */
export function useRowActionsMenu<TRow extends { id: string }>(rows: TRow[]) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [rowId, setRowId] = useState<string | null>(null);
  const row = rows.find((candidate) => candidate.id === rowId) ?? null;
  const close = () => {
    setAnchorEl(null);
    setRowId(null);
  };

  return {
    anchorEl,
    rowId,
    row,
    open: (event: React.MouseEvent<HTMLButtonElement>, notificationUserId: string) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setRowId(notificationUserId);
    },
    close,
    /** Closes the menu, then acts on its row. */
    withRow: (act: (target: TRow) => void) => {
      if (!row) {
        return;
      }
      close();
      act(row);
    },
  };
}
